import { pathFor } from "../../../app/router/routes";
import { HOME_ART } from "../assets";
import { Reveal } from "../Reveal";
import type { MarketingCopy } from "../copy";
import { CtaLink } from "./Cta";

export interface FinalCtaProps {
  t: MarketingCopy;
  /** The heading; the home page's own line when omitted. */
  heading?: string;
  /** The second button's label — "Play Sky Quest free" unless a page says otherwise. */
  playLabel?: string;
}

/**
 * The page's last ask, on the sunset meadow — and the same two actions the hero
 * opened with, so a reader who scrolled the whole page does not have to scroll
 * back up to act on it.
 *
 * ⚠️ NOT *"Join 1000+ schools"*. There is no school count to print, so the
 * subline says what the thirty-minute slot costs a school instead.
 */
export function FinalCta({ t, heading, playLabel }: FinalCtaProps) {
  return (
    <div className="mx-auto mt-24 w-full max-w-[1240px] px-5 sm:px-6 lg:mt-32 lg:px-10">
      <Reveal variant="zoom">
        <div className="relative isolate overflow-hidden rounded-2xl shadow-[0_40px_80px_-40px_color-mix(in_srgb,var(--color-primary-deep)_55%,transparent)] lg:rounded-[3rem]">
          <picture>
            <source media="(max-width: 767px)" srcSet={HOME_ART.sunsetSmall} />
            <img
              src={HOME_ART.sunset}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 -z-10 h-full w-full object-cover object-[60%_center]"
            />
          </picture>
          <div className="flex min-h-[30rem] flex-col items-center px-6 pt-12 pb-44 text-center sm:pt-16 sm:pb-48 lg:min-h-[29rem] lg:pb-16">
            <h2 className="kq-h2 max-w-[22ch] font-dash font-extrabold tracking-tight text-ink">
              {heading ?? t.final.heading}
            </h2>
            <p className="mt-3 max-w-[44ch] font-dash text-dash-md font-bold text-ink-soft">{t.final.sub}</p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <CtaLink href={pathFor("contact-sales")} variant="primary">
                {t.cta.bookSchoolDemo}
              </CtaLink>
              <CtaLink href={pathFor("sky-quest-play")} variant="glass" icon="play">
                {playLabel ?? t.cta.playSkyQuestFree}
              </CtaLink>
            </div>
          </div>
          <img
            src="/images/kalqy-boy-pointing.png"
            alt={t.final.childAlt}
            width={682}
            height={1024}
            loading="lazy"
            decoding="async"
            className="kq-float-sm absolute bottom-[-1.5rem] left-4 h-44 w-auto object-contain drop-shadow-xl sm:left-8 lg:h-64"
          />
        </div>
      </Reveal>
    </div>
  );
}
